import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { X, Pause, WarningOctagon } from "@phosphor-icons/react";

const PAUSE_CATEGORIES = [
    "Mechanical breakdown",
    "Electrical breakdown",
    "Weighbridge breakdown",
    "Awaiting trucks",
    "Product change",
    "Weather",
    "Shift change",
    "Safety incident",
    "Housekeeping",
    "Other",
];

/**
 * PauseExportModal — mirror the RBTG vessel-activity pause flow.
 * Reason + category (breakdown types route to millwright module downstream)
 * + optional location & actions taken.
 */
export default function PauseExportModal({ exportOp, onClose, onSuccess }) {
    const [reason, setReason] = useState("");
    const [category, setCategory] = useState(PAUSE_CATEGORIES[0]);
    const [location, setLocation] = useState("");
    const [actions, setActions] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) return toast.error("Please describe the pause reason.");
        setBusy(true);
        try {
            await api.post(`/tronox/exports/${exportOp.id}/pause`, {
                reason: reason.trim(),
                category,
                location: location.trim() || undefined,
                actions_taken: actions.trim() || undefined,
            });
            toast.success("Operation paused. Time excluded from flow-rate calc until you resume.");
            onSuccess();
        } catch (e2) { toast.error(formatApiError(e2)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="pause-export-modal">
            <div className="bg-slate-900 text-slate-100 rounded-xl w-full max-w-md border border-rose-500/50 shadow-2xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-rose-500/20 border border-rose-500/50 flex items-center justify-center">
                            <Pause size={18} weight="fill" className="text-rose-300" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-400">Load-Out Station</div>
                            <div className="text-lg font-black">Pause operation</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="close-pause-modal">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-4 space-y-4 overflow-y-auto">
                    <div className="rounded-lg border border-rose-500/40 bg-rose-950/40 p-2.5 text-[11px] text-rose-200 flex items-start gap-2">
                        <WarningOctagon size={16} weight="fill" className="text-rose-400 flex-shrink-0 mt-0.5" />
                        <span>Paused time will be <b>excluded</b> from the running-hours used in the flow-rate calc, mirroring RBTG vessel activity. Mechanical / electrical breakdowns route through the millwright module.</span>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase text-slate-400 tracking-wider">Category</label>
                        <div className="grid grid-cols-2 gap-1.5 mt-1">
                            {PAUSE_CATEGORIES.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setCategory(c)}
                                    className={`px-2 py-2 rounded text-[11px] font-bold transition-colors ${category === c ? "bg-rose-600 text-white ring-2 ring-rose-300" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
                                    data-testid={`pause-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase text-slate-400 tracking-wider">Reason *</label>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Conveyor belt torn on route between silo 3 and hopper"
                            className="g-input mt-1"
                            rows={2}
                            data-testid="pause-reason-input"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase text-slate-400 tracking-wider">Location <span className="text-slate-500 font-normal">(optional)</span></label>
                        <input
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            placeholder="e.g. Route between silo 3 and hopper"
                            className="g-input mt-1"
                            data-testid="pause-location-input"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase text-slate-400 tracking-wider">Actions taken <span className="text-slate-500 font-normal">(optional)</span></label>
                        <input
                            value={actions}
                            onChange={(e) => setActions(e.target.value)}
                            placeholder="e.g. Millwright dispatched, ETA 20 min"
                            className="g-input mt-1"
                            data-testid="pause-actions-input"
                        />
                    </div>
                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold" data-testid="pause-cancel">Cancel</button>
                        <button type="submit" disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 py-2 rounded bg-rose-600 hover:bg-rose-500 disabled:bg-slate-700 text-white font-black" data-testid="pause-submit">
                            <Pause size={16} weight="fill" /> {busy ? "Pausing…" : "Pause operation"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
